import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, XCircle, AlertTriangle } from "lucide-react";

interface BillApprovalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  approval: any;
}

export function BillApprovalDialog({
  open,
  onOpenChange,
  approval,
}: BillApprovalDialogProps) {
  const { toast } = useToast();
  const [notes, setNotes] = useState("");
  const [action, setAction] = useState<"approve" | "reject" | null>(null);

  const handleSubmit = async () => {
    // In production, this would call an edge function
    toast({
      title: action === "approve" ? "Bill Approved" : "Bill Rejected",
      description: `The bill has been ${action}d successfully.`,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Bill Approval Review</DialogTitle>
          <DialogDescription>
            Approval Level {approval.approval_level || 1} - Review and approve or reject this bill
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-muted-foreground">Bill ID</Label>
              <p className="font-medium">{approval.bill_id}</p>
            </div>
            <div className="space-y-1">
              <Label className="text-muted-foreground">Status</Label>
              <Badge>{approval.status}</Badge>
            </div>
          </div>

          {approval.required_signers && (
            <div className="space-y-2">
              <Label>Required Approvers</Label>
              <div className="flex flex-wrap gap-2">
                {approval.required_signers.map((signer: string, idx: number) => (
                  <Badge key={idx} variant="outline">
                    {signer}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>Approval Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes about your decision..."
              rows={4}
            />
          </div>

          {approval.timeout_at && (
            <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-500 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-yellow-500">Approval Timeout</p>
                <p className="text-muted-foreground">
                  This approval will timeout at {new Date(approval.timeout_at).toLocaleString()}
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              setAction("reject");
              handleSubmit();
            }}
          >
            <XCircle className="h-4 w-4 mr-2" />
            Reject
          </Button>
          <Button
            onClick={() => {
              setAction("approve");
              handleSubmit();
            }}
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Approve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
